import { queryOptions } from '@tanstack/react-query';

import type { Category } from '@/lib/queries/catalog';

import {
  rankCategoryRows,
  videoWatchHref,
  type BrowseVideo,
  type RankableVideo,
} from '@/lib/queries/browse';
import { supabase } from '@/lib/supabase';

// Watch page (/videos/{slug}) data seam. Render fields only — the Bunny video id
// stays server-side (minted into a token by the video-playback-token function),
// never selected here. The rail reuses browse's pure ranking so "More in
// {category}" is ordered exactly like the browse rows.

export interface VideoRender {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  publishedAt: string | null;
  categoryId: string | null;
  creatorId: string;
  creatorName: string;
  creatorUsername: string | null;
}

export interface VideoRail {
  heading: string;
  videos: BrowseVideo[];
}

function creatorDisplayName(
  creator: { display_name: string | null; username: string | null } | null,
): string {
  return creator?.display_name ?? creator?.username ?? 'ArcheTelos';
}

/** The watch page's content query. Returns null when there is no published video. */
export const watchQueries = {
  render: (slug: string) =>
    queryOptions({
      queryKey: ['watch', 'render', slug] as const,
      queryFn: async (): Promise<VideoRender | null> => {
        const { data, error } = await supabase
          .from('content')
          .select(
            'id, slug, title, excerpt, cover_image_url, cover_image_alt, published_at, category_id, creator_id, creator:profiles!content_creator_id_fkey(display_name, username)',
          )
          .eq('type', 'video')
          .eq('status', 'published')
          .eq('slug', slug)
          .maybeSingle();
        if (error) throw error;
        if (!data) return null;
        return {
          id: data.id,
          slug: data.slug,
          title: data.title,
          excerpt: data.excerpt,
          coverImageUrl: data.cover_image_url,
          coverImageAlt: data.cover_image_alt,
          publishedAt: data.published_at,
          categoryId: data.category_id,
          creatorId: data.creator_id,
          creatorName: creatorDisplayName(data.creator),
          creatorUsername: data.creator?.username ?? null,
        };
      },
    }),

  /**
   * "More in {category}" — this category's videos ranked exactly as browse ranks
   * them (14-day likes desc, recency tiebreak), minus the current title, 3-up.
   * No category (or an empty/singleton bucket) → latest overall, "More from
   * ArcheTelos."
   */
  rail: (categoryId: string | null, currentId: string) =>
    queryOptions({
      queryKey: ['watch', 'rail', categoryId, currentId] as const,
      queryFn: async (): Promise<VideoRail> => {
        const fallback = async (): Promise<VideoRail> => {
          const { data, error } = await supabase
            .from('content')
            .select(
              'id, title, slug, cover_image_url, creator:profiles!content_creator_id_fkey(display_name, username)',
            )
            .eq('type', 'video')
            .eq('status', 'published')
            .neq('id', currentId)
            .order('published_at', { ascending: false })
            .limit(3);
          if (error) throw error;
          const videos: BrowseVideo[] = (data ?? []).map((v) => ({
            id: v.id,
            title: v.title,
            creatorName: creatorDisplayName(v.creator),
            thumbnailUrl: v.cover_image_url,
            durationLabel: null,
            watchHref: videoWatchHref(v.slug),
          }));
          return { heading: 'More from ArcheTelos', videos };
        };

        if (!categoryId) return fallback();

        const [categoryRes, videosRes, likesRes] = await Promise.all([
          supabase.from('categories').select('*').eq('id', categoryId).maybeSingle(),
          supabase
            .from('content')
            .select(
              'id, title, slug, category_id, cover_image_url, published_at, creator:profiles!content_creator_id_fkey(display_name, username)',
            )
            .eq('type', 'video')
            .eq('status', 'published')
            .eq('category_id', categoryId),
          supabase.rpc('get_video_like_counts_14d'),
        ]);
        if (categoryRes.error) throw categoryRes.error;
        if (videosRes.error) throw videosRes.error;
        if (likesRes.error) throw likesRes.error;

        const category = categoryRes.data as Category | null;
        const videos: RankableVideo[] = (videosRes.data ?? []).map((v) => ({
          id: v.id,
          title: v.title,
          slug: v.slug,
          categoryId: v.category_id,
          thumbnailUrl: v.cover_image_url,
          publishedAt: v.published_at,
          creatorName: creatorDisplayName(v.creator),
        }));
        const likeCounts = new Map(
          (likesRes.data ?? []).map((r) => [r.content_id, Number(r.like_count)]),
        );

        const ranked = category ? rankCategoryRows([category], videos, likeCounts)[0]?.videos : [];
        const inCategory = (ranked ?? []).filter((v) => v.id !== currentId).slice(0, 3);
        // Singleton (only the current title) or no category row → fall back.
        if (inCategory.length === 0 || !category) return fallback();
        return { heading: `More in ${category.name}`, videos: inCategory };
      },
    }),
};
